import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Bell, Send, Users, Plus, Trash2, UserPlus, X, Loader2, FolderPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminNotifications() {
  const { stats } = useOutletContext();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState('info');
  const [audience, setAudience] = useState('all');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [sending, setSending] = useState(false);

  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [managingGroup, setManagingGroup] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(true);

  useEffect(() => {
    fetchGroups();
    fetchUsers();
  }, []);

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${API}/admin/groups`);
      setGroups(res.data.groups);
    } catch (err) {
      console.error('Failed to fetch groups');
    } finally {
      setLoadingGroups(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data.users);
    } catch (err) {
      console.error('Failed to fetch users');
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) { toast.error('Group name is required'); return; }
    setCreatingGroup(true);
    try {
      await axios.post(`${API}/admin/groups`, { name: newGroupName, description: newGroupDesc });
      toast.success('Group created');
      setNewGroupName('');
      setNewGroupDesc('');
      fetchGroups();
    } catch (err) {
      toast.error('Failed to create group');
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await axios.delete(`${API}/admin/groups/${groupId}`);
      toast.success('Group deleted');
      setGroups(groups.filter(g => g.id !== groupId));
      if (managingGroup?.id === groupId) setManagingGroup(null);
    } catch (err) {
      toast.error('Failed to delete group');
    }
  };

  const handleAddMember = async (groupId, userId) => {
    try {
      await axios.post(`${API}/admin/groups/${groupId}/members`, { user_ids: [userId] });
      toast.success('Member added');
      fetchGroups();
      if (managingGroup?.id === groupId) {
        const res = await axios.get(`${API}/admin/groups`);
        const updated = res.data.groups.find(g => g.id === groupId);
        setManagingGroup(updated);
      }
    } catch (err) {
      toast.error('Failed to add member');
    }
  };

  const handleRemoveMember = async (groupId, userId) => {
    try {
      await axios.delete(`${API}/admin/groups/${groupId}/members/${userId}`);
      toast.success('Member removed');
      fetchGroups();
      if (managingGroup?.id === groupId) {
        const res = await axios.get(`${API}/admin/groups`);
        const updated = res.data.groups.find(g => g.id === groupId);
        setManagingGroup(updated);
      }
    } catch (err) {
      toast.error('Failed to remove member');
    }
  };

  const handleSend = async () => {
    if (!title || !message) { toast.error('Title and message are required'); return; }

    const payload = { title, message, type };
    if (audience === 'all') {
      payload.send_to_all = true;
    } else if (audience === 'group') {
      if (!selectedGroupId) { toast.error('Please select a group'); return; }
      payload.group_id = selectedGroupId;
    } else if (audience === 'individual') {
      if (!selectedUserId) { toast.error('Please select a user'); return; }
      payload.user_ids = [selectedUserId];
    }

    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/notifications`, payload);
      toast.success(`Notification sent to ${res.data.count} user(s)`);
      setTitle('');
      setMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send notification');
    }
    setSending(false);
  };

  const getMemberNames = (memberIds) => {
    return memberIds.map(id => {
      const u = users.find(u => u.id === id);
      return u ? (u.name || u.email) : id.slice(0, 8);
    });
  };

  return (
    <div data-testid="admin-notifications-page">
      <h1 className="font-heading text-3xl text-white mb-8" data-testid="admin-notifications-title">Notifications & Groups</h1>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Send Notification Form */}
        <div className="xl:col-span-2 glass rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Bell className="text-cyan" size={20} />
            <h2 className="text-lg text-white font-medium">Send Notification</h2>
          </div>

          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="bg-navy-900 border-white/10 text-white" data-testid="notif-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-900 border-white/10 text-white">
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Audience</Label>
                <Select value={audience} onValueChange={setAudience}>
                  <SelectTrigger className="bg-navy-900 border-white/10 text-white" data-testid="notif-audience-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-900 border-white/10 text-white">
                    <SelectItem value="all">All Users ({stats?.total_users || 0})</SelectItem>
                    <SelectItem value="group">User Group</SelectItem>
                    <SelectItem value="individual">Individual User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {audience === 'group' && (
              <div className="space-y-2">
                <Label className="text-slate-300">Select Group</Label>
                <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                  <SelectTrigger className="bg-navy-900 border-white/10 text-white" data-testid="notif-group-select">
                    <SelectValue placeholder="Choose a group..." />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-900 border-white/10 text-white">
                    {groups.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name} ({g.member_ids?.length || 0} members)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {audience === 'individual' && (
              <div className="space-y-2">
                <Label className="text-slate-300">Select User</Label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger className="bg-navy-900 border-white/10 text-white" data-testid="notif-user-select">
                    <SelectValue placeholder="Choose a user..." />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-900 border-white/10 text-white max-h-60">
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-slate-300">Title</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Notification title" className="bg-navy-900 border-white/10 text-white" data-testid="notif-title-input" />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Message</Label>
              <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} placeholder="Notification message..." className="bg-navy-900 border-white/10 text-white resize-none" data-testid="notif-message-input" />
            </div>

            <Button onClick={handleSend} disabled={sending} className="bg-cyan text-navy-950 hover:bg-cyan-300 rounded-full px-8 py-5" data-testid="notif-send-btn">
              <Send size={16} className="mr-2" />
              {sending ? 'Sending...' : 'Send Notification'}
            </Button>
          </div>
        </div>

        {/* Right Sidebar - Groups & Info */}
        <div className="space-y-4">
          {/* Create Group */}
          <Card className="bg-navy-900/40 border-white/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <FolderPlus className="text-cyan" size={18} /> Create Group
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="Group name" className="bg-navy-950 border-white/10 text-white text-sm" data-testid="group-name-input" />
              <Input value={newGroupDesc} onChange={e => setNewGroupDesc(e.target.value)} placeholder="Description (optional)" className="bg-navy-950 border-white/10 text-white text-sm" data-testid="group-desc-input" />
              <Button onClick={handleCreateGroup} disabled={creatingGroup} className="w-full bg-cyan/20 text-cyan hover:bg-cyan/30 rounded-lg text-sm" data-testid="create-group-btn">
                {creatingGroup ? <Loader2 className="animate-spin" size={14} /> : <><Plus size={14} className="mr-1" /> Create Group</>}
              </Button>
            </CardContent>
          </Card>

          {/* Groups List */}
          <Card className="bg-navy-900/40 border-white/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Users className="text-cyan" size={18} /> User Groups ({groups.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingGroups ? (
                <div className="text-center py-4"><Loader2 className="animate-spin text-cyan mx-auto" size={20} /></div>
              ) : groups.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-3">No groups yet</p>
              ) : (
                <div className="space-y-2">
                  {groups.map(g => (
                    <div key={g.id} className="p-3 rounded-lg bg-navy-950/50 border border-white/5 hover:border-white/10 transition-colors">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white text-sm font-medium">{g.name}</span>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-500 hover:text-cyan"
                            onClick={() => setManagingGroup(managingGroup?.id === g.id ? null : g)} data-testid={`manage-group-${g.id}`}>
                            <UserPlus size={12} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-500 hover:text-red-400"
                            onClick={() => handleDeleteGroup(g.id)} data-testid={`delete-group-${g.id}`}>
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </div>
                      <p className="text-slate-500 text-xs">{g.member_ids?.length || 0} members</p>
                      {g.description && <p className="text-slate-500 text-xs mt-0.5">{g.description}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notification Types Info */}
          <Card className="bg-navy-900/40 border-white/5">
            <CardHeader className="pb-3"><CardTitle className="text-white text-base">Notification Types</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-cyan" /> <span className="text-slate-300">Info — General announcements</span></div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-yellow-400" /> <span className="text-slate-300">Warning — Important alerts</span></div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-400" /> <span className="text-slate-300">Success — Positive updates</span></div>
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-purple-400" /> <span className="text-slate-300">Update — Feature updates</span></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Group Member Management Modal */}
      {managingGroup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setManagingGroup(null)}>
          <div className="bg-navy-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="group-members-modal">
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <h3 className="text-white font-medium">Manage: {managingGroup.name}</h3>
              <Button variant="ghost" size="icon" onClick={() => setManagingGroup(null)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </Button>
            </div>

            <div className="p-5 max-h-[60vh] overflow-y-auto">
              {/* Current Members */}
              <h4 className="text-slate-300 text-sm font-medium mb-3">Current Members ({managingGroup.member_ids?.length || 0})</h4>
              {managingGroup.member_ids?.length > 0 ? (
                <div className="space-y-2 mb-6">
                  {getMemberNames(managingGroup.member_ids).map((name, i) => (
                    <div key={managingGroup.member_ids[i]} className="flex items-center justify-between p-2 rounded-lg bg-navy-950/50 border border-white/5">
                      <span className="text-slate-200 text-sm">{name}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-500 hover:text-red-400"
                        onClick={() => handleRemoveMember(managingGroup.id, managingGroup.member_ids[i])} data-testid={`remove-member-${managingGroup.member_ids[i]}`}>
                        <X size={12} />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm mb-6">No members yet</p>
              )}

              {/* Add Members */}
              <h4 className="text-slate-300 text-sm font-medium mb-3">Add Users</h4>
              <div className="space-y-2">
                {users.filter(u => !managingGroup.member_ids?.includes(u.id)).map(u => (
                  <div key={u.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-white/[0.02] transition-colors">
                    <div className="min-w-0">
                      <p className="text-slate-200 text-sm truncate">{u.name}</p>
                      <p className="text-slate-500 text-xs truncate">{u.email}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-cyan hover:bg-cyan/10 flex-shrink-0"
                      onClick={() => handleAddMember(managingGroup.id, u.id)} data-testid={`add-member-${u.id}`}>
                      <Plus size={14} className="mr-1" /> Add
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
